import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Loader2, ExternalLink, Sparkles, Calendar, Package, Activity, AlertTriangle, RotateCcw, Repeat, X, Pause, Play, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { toast } from "sonner";
import { PlanSwitcherDialog } from "@/components/PlanSwitcherDialog";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";
import { PauseSubscriptionDialog } from "@/components/PauseSubscriptionDialog";
import { TrialExtensionCard } from "@/components/TrialExtensionCard";
import { TrialExtensionHistory } from "@/components/TrialExtensionHistory";

const PRICE_LABEL: Record<string, string> = {
  solo_monthly: "Solo · $29 / month",
  solo_yearly: "Solo · $290 / year",
  dealership_monthly: "Dealership · $49 / seat / month",
  dealership_yearly: "Dealership · $490 / seat / year",
};

export function BillingCard() {
  const { mode } = useAuth();
  const { subscription, loading, isActive, isTrialing, isCanceled, isPastDue, refresh } = useSubscription();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseMode, setPauseMode] = useState<"pause" | "resume">("pause");
  const [switchingToYearly, setSwitchingToYearly] = useState(false);
  const [extensionHistoryKey, setExtensionHistoryKey] = useState(0);

  if (mode !== "real") return null;

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || !data?.overviewUrl) {
        toast.error("Could not open portal", { description: error?.message ?? "No subscription yet." });
        return;
      }
      window.open(data.overviewUrl, "_blank", "noopener,noreferrer");
    } finally {
      setOpeningPortal(false);
    }
  };

  const updatePaymentMethod = async () => {
    setUpdatingPayment(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      // Per-subscription URLs include direct payment-method update links
      const subUrl =
        data?.subscriptionUrls?.[0]?.update_subscription_payment_method ??
        data?.subscriptionUrls?.[0]?.update_payment_method ??
        data?.overviewUrl;
      if (error || !subUrl) {
        toast.error("Could not open payment update", { description: error?.message ?? "Try again." });
        return;
      }
      window.open(subUrl, "_blank", "noopener,noreferrer");
    } finally {
      setUpdatingPayment(false);
    }
  };

  const resubscribe = async () => {
    setResubscribing(true);
    try {
      const { data, error } = await supabase.functions.invoke("subscription-resume", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || data?.error) {
        if (data?.error === "requires_checkout") {
          toast.message("Pick a plan to restart", { description: "Your previous period has ended." });
          window.location.href = "/pricing";
          return;
        }
        toast.error("Could not reactivate", { description: data?.error ?? error?.message });
        return;
      }
      toast.success("Subscription reactivated");
      await refresh();
    } finally {
      setResubscribing(false);
    }
  };

  if (loading) return null;

  if (!subscription) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" /> Billing
          </CardTitle>
          <CardDescription>You're on the free preview. Start a trial to unlock paid features.</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-muted-foreground">
            14-day free trial · No charge until day 15 · Cancel anytime
          </div>
          <Link to="/pricing">
            <Button><Sparkles className="h-4 w-4 mr-2" /> See plans</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const statusLabel = isTrialing ? "In trial"
    : isPastDue ? "Past due"
    : isCanceled ? "Canceled — access until period end"
    : isActive ? "Active"
    : subscription.status;
  const statusVariant = isPastDue ? "destructive" : isCanceled ? "secondary" : "default";

  const periodEnd = subscription.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { dateStyle: "medium" })
    : null;

  const planLabel = PRICE_LABEL[subscription.price_id] ?? subscription.price_id;
  const periodEndLabel = isCanceled
    ? "Access ends"
    : isTrialing
      ? "Trial ends / first charge"
      : "Renews";

  const isMonthly = subscription.price_id.endsWith("_monthly");
  const isPaused = subscription.status === "paused";
  const showAnnualUpsell = isMonthly && isActive && !isCanceled && !subscription.cancel_at_period_end && !isPaused;

  const switchToYearly = async () => {
    const yearlyPriceId = subscription.price_id.replace("_monthly", "_yearly");
    setSwitchingToYearly(true);
    const { data, error } = await supabase.functions.invoke("subscription-change-apply", {
      body: { priceId: yearlyPriceId, quantity: subscription.quantity, environment: getPaddleEnvironment() },
    });
    setSwitchingToYearly(false);
    if (error || data?.error) {
      toast.error("Switch failed", { description: data?.error ?? error?.message });
      return;
    }
    toast.success("Switched to yearly · 2 months free");
    await refresh();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" /> Billing
        </CardTitle>
        <CardDescription>Manage your subscription, payment method, and invoices.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Subscription status panel */}
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <Package className="h-3.5 w-3.5" /> Plan
              </div>
              <p className="text-sm font-medium leading-tight">{planLabel}</p>
              {subscription.quantity > 1 && (
                <p className="text-xs text-muted-foreground">{subscription.quantity} seats</p>
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <Activity className="h-3.5 w-3.5" /> Status
              </div>
              <Badge variant={statusVariant as any} className="text-xs">{statusLabel}</Badge>
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" /> {periodEndLabel}
              </div>
              <p className="text-sm font-medium leading-tight">{periodEnd ?? "—"}</p>
            </div>
          </div>
        </div>

        {isTrialing && (
          <TrialExtensionCard onExtended={() => setExtensionHistoryKey((k) => k + 1)} />
        )}

        <TrialExtensionHistory refreshKey={extensionHistoryKey} />

        {showAnnualUpsell && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 p-3">
            <div className="flex items-start gap-2 min-w-0">
              <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">Save ~17% with yearly</p>
                <p className="text-xs text-muted-foreground">Get 2 months free. Prorated immediately.</p>
              </div>
            </div>
            <Button size="sm" onClick={switchToYearly} disabled={switchingToYearly}>
              {switchingToYearly && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Switch
            </Button>
          </div>
        )}

        {isPaused && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex items-start gap-2 min-w-0">
              <Pause className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium">Subscription paused</p>
                <p className="text-xs text-muted-foreground">Resume anytime to restart billing and access.</p>
              </div>
            </div>
            <Button size="sm" onClick={() => { setPauseMode("resume"); setPauseOpen(true); }}>
              <Play className="h-4 w-4 mr-2" /> Resume
            </Button>
          </div>
        )}

        {isPastDue && (
          <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30 space-y-2">
            <div className="flex items-start gap-2 text-xs text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Your payment failed. Paddle is retrying — update your card now to keep dashboard access.</span>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={updatePaymentMethod}
              disabled={updatingPayment}
            >
              {updatingPayment
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <CreditCard className="h-4 w-4 mr-2" />}
              Update payment method
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {(isCanceled || subscription.cancel_at_period_end) && (
            <Button onClick={resubscribe} disabled={resubscribing} size="sm">
              {resubscribing
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <RotateCcw className="h-4 w-4 mr-2" />}
              Reactivate
            </Button>
          )}
          <Button onClick={openPortal} disabled={openingPortal} variant="outline" size="sm">
            {openingPortal ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            Manage subscription
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSwitcherOpen(true)} disabled={!isActive || isPaused}>
            <Repeat className="h-4 w-4 mr-2" /> Switch plan
          </Button>
          {isActive && !isCanceled && !subscription.cancel_at_period_end && !isPaused && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setPauseMode("pause"); setPauseOpen(true); }}
              >
                <Pause className="h-4 w-4 mr-2" /> Pause
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCancelOpen(true)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="h-4 w-4 mr-2" /> Cancel
              </Button>
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Payments handled by Paddle.com (Merchant of Record).
        </p>
        <PlanSwitcherDialog open={switcherOpen} onOpenChange={setSwitcherOpen} />
        <CancelSubscriptionDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          onPauseInstead={() => { setPauseMode("pause"); setPauseOpen(true); }}
        />
        <PauseSubscriptionDialog open={pauseOpen} onOpenChange={setPauseOpen} mode={pauseMode} />
      </CardContent>
    </Card>
  );
}